class RbFitFinder extends HTMLElement {
  constructor() {
    super();
    this.onToggle = this.onToggle.bind(this);
    this.onCalculate = this.onCalculate.bind(this);
    this.onAddToCart = this.onAddToCart.bind(this);
    this.onSaveConfiguration = this.onSaveConfiguration.bind(this);
    this.onEditMeasures = this.onEditMeasures.bind(this);
    this.onEdit = this.onEdit.bind(this);
    this.onReset = this.onReset.bind(this);
    this.onMeasureInput = this.onMeasureInput.bind(this);
    this.onMeasureBlur = this.onMeasureBlur.bind(this);
    this.pendingResult = null;
    this.currentStep = 'measure';
    this.mainVariantAvailable = true;
  }

  connectedCallback() {
    this.rules = this.parseJson('[data-rules]');
    this.extensionPrices = this.parseJson('[data-extension-prices]');
    this.strings = this.parseJson('[data-fit-finder-strings]');

    this.headerToggle = this.querySelector('[data-action="toggle"]');
    this.headerAction = this.querySelector('[data-fit-finder-header-action]');
    this.headerChevron = this.querySelector('.rb-fit-finder__chevron');
    this.headerTitle = this.querySelector('[data-fit-finder-header-title]');
    this.headerSubtitle = this.querySelector('[data-fit-finder-header-subtitle]');
    this.headerEdit = this.querySelector('[data-action="edit"]');
    this.blockRoot = this.closest('[data-fit-finder-block]');
    this.resetWrap = this.blockRoot?.querySelector('[data-fit-finder-reset-wrap]');
    this.headerReset = this.resetWrap?.querySelector('[data-action="reset"]');
    this.iconRuler = this.querySelector('.rb-fit-finder__header-icon--ruler');
    this.iconSaved = this.querySelector('.rb-fit-finder__header-icon--saved');
    this.body = this.querySelector('[data-fit-finder-body]');
    this.configuredBar = this.querySelector('[data-fit-finder-configured-bar]');
    this.configuredPriceEl = this.querySelector('[data-fit-finder-configured-price]');

    this.stepMeasure = this.querySelector('[data-step="measure"]');
    this.stepResult = this.querySelector('[data-step="result"]');
    this.resultTitleEl = this.querySelector('[data-fit-finder-result-title]');
    this.resultSubtitleEl = this.querySelector('[data-fit-finder-result-subtitle]');
    this.resultSupportBlock = this.querySelector('[data-fit-finder-result-support-block]');
    this.resultSupportEl = this.querySelector('[data-fit-finder-result-support]');
    this.resultSupportMeasures = this.querySelector('[data-fit-finder-support-measures]');
    this.resultSupportMeasuresText = this.querySelector('[data-fit-finder-support-measures-text]');
    this.resultSupportCta = this.querySelector('[data-fit-finder-support-cta]');
    this.configBoxEl = this.querySelector('[data-fit-finder-config-box]');
    this.blockAtcBtn = this.querySelector('[data-fit-finder-block-atc]');
    this.blockSaveBtn = this.querySelector('[data-action="save-configuration"]');
    this.progressBar = this.querySelector('[data-fit-finder-progress-bar]');
    this.progressBarFill = this.querySelector('[data-fit-finder-progress-bar-fill]');
    this.headerToggle?.addEventListener('click', this.onToggle);
    this.querySelector('[data-action="calculate"]')?.addEventListener('click', this.onCalculate);
    this.blockAtcBtn?.addEventListener('click', this.onAddToCart);
    this.blockSaveBtn?.addEventListener('click', this.onSaveConfiguration);
    this.addEventListener('click', this.onEditMeasures);
    this.headerEdit?.addEventListener('click', this.onEdit);
    this.headerReset?.addEventListener('click', this.onReset);

    this.mainVariantAvailable = this.readMainVariantAvailable();
    this.bindMeasureValidation();
    this.bindVideoDuration();
    this.bindVideoAutoplay();
    this.ensureSectionMainVariant();
    this.bindVariantPriceSync();
    this.applyCatalogState();
    this.updateBlockVisibility();
    this.restoreFromSession();
  }

  readMainVariantAvailable() {
    const fromAttr = this.getAttribute('data-main-available');
    if (fromAttr === 'true') return true;
    if (fromAttr === 'false') return false;
    return this.extensionPrices?.main?.available !== false;
  }

  isMainVariantAvailable() {
    return this.mainVariantAvailable !== false;
  }

  isExtensionCatalogReady() {
    return (
      this.getAttribute('data-catalog-ready') === 'true' ||
      this.extensionPrices?.catalog?.ready === true
    );
  }

  isCatalogReady() {
    return this.isExtensionCatalogReady() && this.isMainVariantAvailable();
  }

  setMainVariantAvailable(available) {
    const next = available !== false;
    const changed = this.mainVariantAvailable !== next;
    this.mainVariantAvailable = next;
    this.setAttribute('data-main-available', next ? 'true' : 'false');
    if (this.extensionPrices?.main) {
      this.extensionPrices.main.available = next;
    }
    if (changed) {
      this.applyCatalogState();
      this.updateBlockVisibility();
    }
  }

  updateBlockVisibility() {
    const block = this.closest('[data-fit-finder-block]');
    if (block) block.hidden = !this.isCatalogReady();
  }

  isVariantPurchasable(variant) {
    if (!variant?.id) return false;
    if (!variant.available) return false;

    const productInfo = this.closest('product-info');
    const submitBtn = productInfo?.querySelector('.product-form__submit');
    if (submitBtn?.disabled) return false;

    return true;
  }

  profileCatalogEntry(profile) {
    const key = String(profile || '').toLowerCase();
    return this.extensionPrices?.catalog?.[key] || null;
  }

  profileIsActive(profile) {
    const entry = this.profileCatalogEntry(profile);
    return Boolean(entry?.configured && entry?.available);
  }

  getDefaultIncludedProfile() {
    const preset = this.extensionPrices?.catalog?.defaultIncludedProfile;
    if (preset && this.profileCatalogEntry(preset)?.includedByDefault) {
      return preset;
    }
    for (const profile of ['c', 'a', 'b']) {
      const entry = this.profileCatalogEntry(profile);
      if (entry?.configured && entry?.available && entry?.includedByDefault) {
        return profile;
      }
    }
    return null;
  }

  configIsFulfillable(config) {
    if (!this.isCatalogReady()) return false;
    if ((config.a || 0) > 0 && !this.profileIsActive('a')) return false;
    if ((config.b || 0) > 0 && !this.profileIsActive('b')) return false;
    if ((config.cExtra || 0) > 0 && !this.profileIsActive('c')) return false;
    return true;
  }

  applyCatalogState() {
    if (!this.isCatalogReady() && this.dataset.state !== 'idle') {
      this.reset();
    }
  }

  disconnectedCallback() {
    this.headerToggle?.removeEventListener('click', this.onToggle);
    this.querySelector('[data-action="calculate"]')?.removeEventListener('click', this.onCalculate);
    this.blockAtcBtn?.removeEventListener('click', this.onAddToCart);
    this.blockSaveBtn?.removeEventListener('click', this.onSaveConfiguration);
    this.removeEventListener('click', this.onEditMeasures);
    this.headerEdit?.removeEventListener('click', this.onEdit);
    this.headerReset?.removeEventListener('click', this.onReset);
    this.measureInputs?.forEach((input) => {
      input.removeEventListener('input', this.onMeasureInput);
      input.removeEventListener('blur', this.onMeasureBlur);
    });
  }

  bindMeasureValidation() {
    this.measureInputs = this.querySelectorAll('[data-input]');
    this.measureFieldErrors = {};
    this.measureInputs.forEach((input) => {
      const key = input.dataset.input;
      if (key) {
        this.measureFieldErrors[key] = this.querySelector(`[data-fit-finder-field-error="${key}"]`);
      }
      input.addEventListener('input', this.onMeasureInput);
      input.addEventListener('blur', this.onMeasureBlur);
    });
  }

  onMeasureInput(event) {
    this.clearFieldValidation(event.currentTarget);
  }

  onMeasureBlur(event) {
    const input = event.currentTarget;
    if (!input?.value) return;
    this.showFieldValidation(input);
  }

  getMeasureDigitCount(value) {
    return String(Math.abs(Math.round(value))).length;
  }

  isPlausibleMeasureValue(value) {
    const digits = this.getMeasureDigitCount(value);
    return digits >= 2 && digits <= 3;
  }

  getMeasureValidationError(input) {
    const raw = input?.value;
    if (raw === '' || raw == null) {
      return this.strings?.measureRequired || '';
    }

    const value = Math.round(Number(raw));
    if (!Number.isFinite(value) || value <= 0) {
      return this.strings?.measureRequired || '';
    }

    if (!this.isPlausibleMeasureValue(value)) {
      return this.strings?.measureImplausible || '';
    }

    return null;
  }

  isMeasureInputValid(input) {
    return !this.getMeasureValidationError(input);
  }

  showFieldValidation(input) {
    const message = this.getMeasureValidationError(input);
    if (!message) {
      this.clearFieldValidation(input);
      return true;
    }

    input.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
    const errorEl = this.measureFieldErrors?.[input.dataset.input];
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      input.setAttribute('aria-describedby', errorEl.id);
    }
    return false;
  }

  clearFieldValidation(input) {
    if (!input) return;
    input.classList.remove('is-invalid');
    input.removeAttribute('aria-invalid');
    const errorId = input.getAttribute('aria-describedby');
    if (errorId) {
      input.removeAttribute('aria-describedby');
    }
    const errorEl = this.measureFieldErrors?.[input.dataset.input];
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }

  clearMeasureValidation() {
    this.measureInputs?.forEach((input) => this.clearFieldValidation(input));
  }

  showMeasureValidation() {
    let firstInvalid = null;

    this.measureInputs?.forEach((input) => {
      if (this.showFieldValidation(input)) return;
      if (!firstInvalid) firstInvalid = input;
    });

    firstInvalid?.focus();
    return !firstInvalid;
  }

  bindVideoDuration() {
    const labelEl = this.querySelector('[data-fit-finder-video-label]');
    const video = this.querySelector('.rb-fit-finder__video-media');
    if (!labelEl || !video || labelEl.dataset.duration) return;

    const updateDuration = () => {
      if (!video.duration || Number.isNaN(video.duration)) return;
      const totalSec = Math.round(video.duration);
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      const secStr = sec < 10 ? `0${sec}` : String(sec);
      const duration = `${min}:${secStr}`;
      const template = this.strings.videoTeaser || this.strings.videoTeaserNoDuration || '';
      labelEl.textContent = this.fillTemplate(template, { duration, DURATION: duration });
    };

    video.addEventListener('loadedmetadata', updateDuration, { once: true });
  }

  bindVideoAutoplay() {
    const details = this.querySelector('.rb-fit-finder__video');
    const video = this.querySelector('.rb-fit-finder__video-media');
    if (!details || !video) return;

    details.addEventListener('toggle', () => {
      if (details.open) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  parseJson(selector) {
    try {
      const el = this.querySelector(selector) || document.querySelector(selector);
      return JSON.parse(el?.textContent || '{}');
    } catch {
      return {};
    }
  }

  getMeasurements() {
    const bellyRaw = this.querySelector('[data-input="belly"]')?.value;
    const thighRaw = this.querySelector('[data-input="thigh"]')?.value;
    const belly = bellyRaw === '' || bellyRaw == null ? NaN : Math.round(Number(bellyRaw));
    const thigh = thighRaw === '' || thighRaw == null ? NaN : Math.round(Number(thighRaw));
    return { belly, thigh };
  }

  hasValidMeasurements() {
    const thighInput = this.querySelector('[data-input="thigh"]');
    const bellyInput = this.querySelector('[data-input="belly"]');
    return this.isMeasureInputValid(thighInput) && this.isMeasureInputValid(bellyInput);
  }

  setMeasureValues(belly, thigh) {
    const bellyInput = this.querySelector('[data-input="belly"]');
    const thighInput = this.querySelector('[data-input="thigh"]');
    if (bellyInput) bellyInput.value = belly != null && !Number.isNaN(belly) ? belly : '';
    if (thighInput) thighInput.value = thigh != null && !Number.isNaN(thigh) ? thigh : '';
  }

  configFits(config, belly, thigh) {
    return thigh <= config.maxThigh && belly <= config.maxBelly;
  }

  getConfigById(configId) {
    return (this.rules?.configs || []).find((config) => config.id === configId) || null;
  }

  getSelectionRules() {
    return this.rules?.selection || {};
  }

  getMeasureContext(belly, thigh) {
    const thresholds = this.rules?.thresholds || {};
    const bellyStd = thresholds.bellyStandard ?? 122;
    const thighStd = thresholds.thighStandard ?? 74;

    return {
      bellyStd,
      thighStd,
      bellyOnly: belly > bellyStd && thigh <= thighStd,
      thighOnly: belly <= bellyStd && thigh > thighStd,
      bothExtended: belly > bellyStd && thigh > thighStd,
      bellyLed: belly > bellyStd && thigh > thighStd && belly - bellyStd >= thigh - thighStd,
      thighLed: belly > bellyStd && thigh > thighStd && thigh - thighStd > belly - bellyStd,
    };
  }

  stepLookup(measurement, stepIds, dimension) {
    if (!Array.isArray(stepIds) || !stepIds.length) return null;

    for (const configId of stepIds) {
      const config = this.getConfigById(configId);
      if (!config) continue;
      const limit = dimension === 'belly' ? config.maxBelly : config.maxThigh;
      if (measurement <= limit) return configId;
    }

    return null;
  }

  applyThighUpgrades(configId, thigh) {
    const fromConfig = this.getConfigById(configId);
    if (!fromConfig || thigh <= fromConfig.maxThigh) return configId;

    const upgrade = (this.getSelectionRules().thighUpgrades || []).find((rule) => rule.from === configId);
    return upgrade?.to || configId;
  }

  applyBellyUpgradesFromThighPath(configId, belly, thigh) {
    const fromConfig = this.getConfigById(configId);
    if (!fromConfig || belly <= fromConfig.maxBelly) return configId;

    const bellySteps = this.getSelectionRules().bellySteps || [];
    const startIdx = Math.max(0, bellySteps.indexOf(configId));

    for (let i = startIdx + 1; i < bellySteps.length; i += 1) {
      const candidate = this.getConfigById(bellySteps[i]);
      if (candidate && belly <= candidate.maxBelly && this.configFits(candidate, belly, thigh)) {
        return bellySteps[i];
      }
    }

    return configId;
  }

  resolveCombinedSimple(belly, thigh) {
    const combinedSimple = this.getSelectionRules().combinedSimple || [];

    for (const configId of combinedSimple) {
      const config = this.getConfigById(configId);
      if (config && belly <= config.maxBelly && thigh <= config.maxThigh) return configId;
    }

    return null;
  }

  resolveConfigTargetId(belly, thigh) {
    const context = this.getMeasureContext(belly, thigh);
    const selection = this.getSelectionRules();

    if (belly <= context.bellyStd && thigh <= context.thighStd) return 'std';
    if (context.bellyOnly) return this.stepLookup(belly, selection.bellySteps, 'belly');
    if (context.thighOnly) return this.stepLookup(thigh, selection.thighSteps, 'thigh');

    const combined = this.resolveCombinedSimple(belly, thigh);
    if (combined) return combined;

    if (context.bothExtended) {
      if (context.bellyLed) {
        const base = this.stepLookup(belly, selection.bellySteps, 'belly');
        return this.applyThighUpgrades(base, thigh);
      }
      if (context.thighLed) {
        const base = this.stepLookup(thigh, selection.thighSteps, 'thigh');
        return this.applyBellyUpgradesFromThighPath(base, belly, thigh);
      }
      const base = this.stepLookup(belly, selection.bellySteps, 'belly');
      return this.applyThighUpgrades(base, thigh);
    }

    return this.stepLookup(belly, selection.bellySteps, 'belly');
  }

  selectConfig(belly, thigh) {
    const fitting = (this.rules.configs || [])
      .filter((config) => this.configFits(config, belly, thigh))
      .filter((config) => this.configIsFulfillable(config));
    if (!fitting.length) return null;

    const targetId = this.resolveConfigTargetId(belly, thigh);
    const preferred = fitting.find((config) => config.id === targetId);
    if (preferred) return preferred;

    return fitting
      .slice()
      .sort((a, b) => a.maxBelly - b.maxBelly || a.maxThigh - b.maxThigh)[0];
  }

  parseVariantId(key) {
    const letter = String(key).replace(/^variant/i, '').toLowerCase();
    if (!this.profileIsActive(letter)) return null;
    const id = parseInt(this.getAttribute(`data-variant-${letter}`), 10);
    return id && !Number.isNaN(id) ? id : null;
  }

  getSectionProductId() {
    return parseInt(this.extensionPrices?.main?.productId, 10) || parseInt(this.getAttribute('data-product-id'), 10) || null;
  }

  setMainVariantId(variantId, priceCents) {
    const id = parseInt(variantId, 10);
    if (!id || Number.isNaN(id) || this.getAddonVariantIds().has(id)) return;
    this.setAttribute('data-main-variant-id', String(id));
    this.dataset.mainVariantId = String(id);
    if (this.extensionPrices?.main) {
      this.extensionPrices.main.variantId = id;
      if (typeof priceCents === 'number') this.extensionPrices.main.priceCents = priceCents;
    }
  }

  getAddonVariantIds() {
    const ids = new Set();
    ['a', 'b', 'c'].forEach((letter) => {
      const attrId = parseInt(this.getAttribute(`data-variant-${letter}`), 10);
      const jsonId = parseInt(this.extensionPrices?.[letter]?.variantId, 10);
      [attrId, jsonId].forEach((id) => {
        if (id && !Number.isNaN(id)) ids.add(id);
      });
    });
    return ids;
  }

  resolveMainVariantId() {
    const addonIds = this.getAddonVariantIds();
    const sectionVariantId = parseInt(this.extensionPrices?.main?.variantId, 10);
    const liveVariantId = parseInt(this.getAttribute('data-main-variant-id'), 10);

    for (const id of [liveVariantId, sectionVariantId]) {
      if (id && !Number.isNaN(id) && !addonIds.has(id)) return id;
    }
    return null;
  }

  profileEntry(profile) {
    const entry = this.extensionPrices?.[profile.toLowerCase()];
    if (entry && typeof entry === 'object') return entry;
    if (typeof entry === 'number') return { title: '', priceCents: entry };
    return { title: '', priceCents: 0 };
  }

  profileTitle(profile) {
    const title = this.profileEntry(profile).title;
    return title || profile.toUpperCase();
  }

  priceCentsForProfile(profile) {
    const cents = this.profileEntry(profile).priceCents;
    return typeof cents === 'number' && cents > 0 ? cents : 0;
  }

  fillTemplate(template, vars) {
    if (!template) return '';
    return Object.entries(vars).reduce((text, [key, value]) => {
      const safeValue = value == null ? '' : String(value);
      return text
        .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), safeValue)
        .replace(new RegExp(`@@${key.toUpperCase()}@@`, 'g'), safeValue);
    }, template);
  }

  buildCartItems(result) {
    const items = [];
    const profiles = [
      { qty: result.aQty, key: 'variantA', profile: 'a' },
      { qty: result.bQty, key: 'variantB', profile: 'b' },
      { qty: result.cExtra, key: 'variantC', profile: 'c' },
    ];

    profiles.forEach(({ qty, key, profile }) => {
      if (qty <= 0) return;
      const id = this.parseVariantId(key);
      if (!id) return;
      items.push({
        id,
        quantity: qty,
        profile: profile.toUpperCase(),
        title: this.profileTitle(profile),
        priceCents: this.priceCentsForProfile(profile),
      });
    });

    return items;
  }

  formatMoney(cents) {
    if (cents == null || cents < 0) return '';
    if (window.Shopify?.formatMoney && this.extensionPrices?.moneyFormat) {
      return window.Shopify.formatMoney(cents, this.extensionPrices.moneyFormat);
    }
    const locale = (this.extensionPrices?.locale || document.documentElement.lang || 'de-DE').replace(
      '_',
      '-'
    );
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.extensionPrices?.currency || 'EUR',
    }).format(cents / 100);
  }

  evaluate() {
    const { belly, thigh } = this.getMeasurements();
    const config = this.selectConfig(belly, thigh);
    const needsSupport = !config;

    const aQty = needsSupport ? 0 : config.a || 0;
    const bQty = needsSupport ? 0 : config.b || 0;
    const cExtra = needsSupport ? 0 : config.cExtra || 0;
    const ruleUsesIncluded = needsSupport ? false : config.useIncludedC !== false;
    const includedProfile = ruleUsesIncluded ? this.getDefaultIncludedProfile() : null;
    const resultType = !needsSupport && config.id === 'std' ? 'fits_standard' : 'custom_profile';

    const result = {
      belly,
      thigh,
      configId: config?.id || null,
      resultType,
      needsSupport,
      usesIncludedC: Boolean(includedProfile),
      includedProfile,
      aQty,
      bQty,
      cExtra,
      replaceIncluded: !needsSupport && !ruleUsesIncluded && (aQty > 0 || bQty > 0),
    };

    result.cartItems = this.buildCartItems(result);
    result.extensionSummary = this.buildExtensionSummary(result, result.cartItems);
    result.pricing = this.buildPricing(result);
    return result;
  }

  buildExtensionSummary(result, cartItems) {
    let totalCents = 0;
    let missingConfig = false;

    if (result.aQty > 0 && !this.parseVariantId('variantA')) missingConfig = true;
    if (result.bQty > 0 && !this.parseVariantId('variantB')) missingConfig = true;
    if (result.cExtra > 0 && !this.parseVariantId('variantC')) missingConfig = true;

    cartItems.forEach((item) => {
      totalCents += (item.priceCents || 0) * (item.quantity || 1);
    });

    return {
      calculated: true,
      totalCents,
      formatted: totalCents > 0 ? this.formatMoney(totalCents) : '',
      hasAddons: cartItems.length > 0,
      missingConfig,
      needsSupport: result.needsSupport,
      usesIncludedCOnly: result.resultType === 'fits_standard',
      configId: result.configId,
    };
  }

  buildPricing(result) {
    const mainCents = this.extensionPrices?.main?.priceCents || 0;
    const adjustmentCents = result.extensionSummary?.totalCents || 0;
    const grandTotalCents = mainCents + adjustmentCents;
    return {
      mainCents,
      adjustmentCents,
      grandTotalCents,
      mainFormatted: this.formatMoney(mainCents),
      adjustmentFormatted: adjustmentCents > 0 ? this.formatMoney(adjustmentCents) : '',
      grandTotalFormatted: this.formatMoney(grandTotalCents),
    };
  }

  formatMeasuresSummary(belly, thigh) {
    const template = this.strings.measuresSummary || '{{ thigh }} · {{ belly }}';
    return template
      .replace(/\{\{\s*belly\s*\}\}/g, `${belly} cm`)
      .replace(/\{\{\s*thigh\s*\}\}/g, `${thigh} cm`)
      .replace(/@@BELLY@@/g, `${belly} cm`)
      .replace(/@@THIGH@@/g, `${thigh} cm`);
  }

  formatIncludedMainSubtitle(result) {
    if (!result?.usesIncludedC) return '';
    return this.strings.configMainSubtitle || '';
  }

  mountMainTitle(titleEl) {
    if (!titleEl) return;

    titleEl.textContent = '';
    const tpl = this.querySelector('template[data-fit-finder-main-title]');
    if (tpl?.content?.childNodes.length) {
      titleEl.appendChild(tpl.content.cloneNode(true));
      return;
    }

    titleEl.textContent = this.extensionPrices?.main?.title || '';
  }

  renderConfigBox(result) {
    if (result.needsSupport) return '';

    const adjustmentTitle = this.strings.configAdjustmentTitle || '';
    const includedLabel = this.strings.configIncluded || '';
    const totalLabel = this.strings.configTotal || '';
    const pricing = result.pricing;
    const hasPaidAdjustment = pricing.adjustmentCents > 0;
    const measures = this.formatMeasuresSummary(result.belly, result.thigh);
    const editLabel = this.strings.headerEdit || '';
    const includedSubtitle = this.formatIncludedMainSubtitle(result);

    let adjustmentValue = '';
    if (hasPaidAdjustment) {
      adjustmentValue = pricing.adjustmentFormatted;
    } else if (result.resultType === 'fits_standard' || !result.extensionSummary.hasAddons) {
      adjustmentValue = `<span class="rb-fit-finder__config-included">${includedLabel}</span>`;
    }

    const mainSubtitleHtml = includedSubtitle
      ? `<span class="rb-fit-finder__config-line-sub">${includedSubtitle}</span>`
      : '';

    return `
      <div class="rb-fit-finder__config-box-inner">
        <ul class="rb-fit-finder__config-lines">
          <li class="rb-fit-finder__config-line">
            <span class="rb-fit-finder__config-line-label">
              <span class="rb-fit-finder__config-line-title" data-fit-finder-main-title-slot></span>
              ${mainSubtitleHtml}
            </span>
            <span class="rb-fit-finder__config-line-price">${pricing.mainFormatted}</span>
          </li>
          <li class="rb-fit-finder__config-line">
            <span class="rb-fit-finder__config-line-label">
              <span class="rb-fit-finder__config-line-title">${adjustmentTitle}</span>
              <span class="rb-fit-finder__config-measures-row">
                <span class="rb-fit-finder__config-measures">${measures}</span>
                <button type="button" class="link link--text rb-fit-finder__config-measures-edit" data-action="edit-measures">
                  ${editLabel}
                </button>
              </span>
            </span>
            <span class="rb-fit-finder__config-line-price">${adjustmentValue}</span>
          </li>
        </ul>
        <p class="rb-fit-finder__config-total">
          <span>${totalLabel}</span>
          <span class="rb-fit-finder__config-total-price">${pricing.grandTotalFormatted}</span>
        </p>
      </div>
    `;
  }

  renderResultStep(result) {
    this.pendingResult = result;
    const isStandard = result.resultType === 'fits_standard' && !result.needsSupport;

    if (this.resultTitleEl) {
      if (result.needsSupport) {
        this.resultTitleEl.textContent = this.strings.resultSupportTitle || '';
      } else if (isStandard) {
        this.resultTitleEl.textContent = this.strings.resultFitsTitle || '';
      } else {
        this.resultTitleEl.textContent = this.strings.resultCustomTitle || '';
      }
    }
    if (this.resultSubtitleEl) {
      if (result.needsSupport) {
        this.resultSubtitleEl.textContent = this.strings.resultSupportSubtitle || '';
      } else if (isStandard) {
        this.resultSubtitleEl.textContent = this.strings.resultFitsSubtitle || '';
      } else {
        this.resultSubtitleEl.textContent = this.strings.resultCustomSubtitle || '';
      }
    }
    if (this.resultSupportBlock) {
      const showSupport = Boolean(result.needsSupport);
      this.resultSupportBlock.hidden = !showSupport;
      if (this.resultSupportEl) {
        this.resultSupportEl.textContent =
          showSupport && this.strings.supportHint ? this.strings.supportHint : '';
      }
      if (this.resultSupportMeasures && this.resultSupportMeasuresText) {
        if (showSupport) {
          this.resultSupportMeasuresText.textContent = this.formatMeasuresSummary(
            result.belly,
            result.thigh
          );
          this.resultSupportMeasures.hidden = false;
        } else {
          this.resultSupportMeasuresText.textContent = '';
          this.resultSupportMeasures.hidden = true;
        }
      }
      if (this.resultSupportCta) {
        const url = (this.strings.supportCtaUrl || '').trim();
        if (showSupport && url) {
          this.resultSupportCta.href = url;
          this.resultSupportCta.textContent = this.strings.supportCta || '';
          this.resultSupportCta.hidden = false;
        } else {
          this.resultSupportCta.hidden = true;
        }
      }
    }
    if (this.configBoxEl) {
      const html = result.needsSupport ? '' : this.renderConfigBox(result);
      this.configBoxEl.innerHTML = html;
      if (html) {
        this.mountMainTitle(this.configBoxEl.querySelector('[data-fit-finder-main-title-slot]'));
      }
      this.configBoxEl.hidden = !html;
    }

    const hideActions = Boolean(result.needsSupport);
    if (this.blockAtcBtn) {
      this.blockAtcBtn.hidden = hideActions;
    }
    if (this.blockSaveBtn) {
      this.blockSaveBtn.hidden = hideActions;
    }
  }

  ensureSectionMainVariant() {
    const sectionVariantId = parseInt(this.extensionPrices?.main?.variantId, 10);
    if (!sectionVariantId || Number.isNaN(sectionVariantId)) return;

    const current = parseInt(this.getAttribute('data-main-variant-id'), 10);
    if (!current || Number.isNaN(current) || this.getAddonVariantIds().has(current)) {
      this.setMainVariantId(sectionVariantId, this.extensionPrices?.main?.priceCents);
    }
  }

  bindVariantPriceSync() {
    const syncFromVariant = (variant) => {
      if (!variant?.id) return;
      const sectionProductId = this.getSectionProductId();
      if (sectionProductId && variant.product_id && variant.product_id !== sectionProductId) return;

      this.setMainVariantAvailable(this.isVariantPurchasable(variant));
      if (!this.isMainVariantAvailable()) return;

      this.setMainVariantId(variant.id, variant.price);
      if (this.pendingResult && !this.pendingResult.needsSupport) {
        this.pendingResult.pricing = this.buildPricing(this.pendingResult);
        if (this.currentStep === 'result') this.renderResultStep(this.pendingResult);
      }
    };

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        syncFromVariant(event?.data?.variant);
      });
    }
  }

  updateProgress(step) {
    if (this.progressBarFill) {
      this.progressBarFill.classList.toggle('is-complete', step === 'result');
    }

    if (this.progressBar) {
      const stepValue = step === 'result' ? 2 : 1;
      this.progressBar.setAttribute('aria-valuenow', String(stepValue));
      const label =
        step === 'result' ? this.strings?.stepResult : this.strings?.stepMeasure;
      if (label) this.progressBar.setAttribute('aria-label', label);
    }
  }

  showStep(step) {
    if (step !== 'measure') {
      const video = this.querySelector('.rb-fit-finder__video');
      if (video) video.open = false;
    }
    this.currentStep = step;
    if (this.stepMeasure) this.stepMeasure.hidden = step !== 'measure';
    if (this.stepResult) this.stepResult.hidden = step !== 'result';
    this.updateProgress(step);
    this.updateHeaderCopy();
    this.updateHeaderIcons();
  }

  updateHeaderCopy() {
    if (!this.headerTitle || !this.headerSubtitle) return;

    const state = this.dataset.state;

    if (state === 'configured' && this.pendingResult) {
      const { belly, thigh } = this.pendingResult;
      this.headerTitle.textContent = this.strings.headerSavedTitle || '';
      this.headerSubtitle.textContent = this.formatMeasuresSummary(belly, thigh);
      return;
    }

    if (state === 'expanded') {
      this.headerTitle.textContent =
        this.currentStep === 'result'
          ? this.strings.stepResult || ''
          : this.strings.stepMeasure || '';
      this.headerSubtitle.textContent = '';
      return;
    }

    this.headerTitle.textContent = this.strings.headerTitle || '';
    this.headerSubtitle.textContent = this.strings.headerSubtitle || '';
  }

  updateHeaderIcons() {
    const state = this.dataset.state;
    const showCheck =
      state === 'configured' || (state === 'expanded' && this.currentStep === 'result');

    if (this.iconRuler) this.iconRuler.hidden = showCheck;
    if (this.iconSaved) this.iconSaved.hidden = !showCheck;
  }

  setUiState(state) {
    this.dataset.state = state;
    const isConfigured = state === 'configured';
    const isExpanded = state === 'expanded';

    this.classList.toggle('is-configured', isConfigured);
    this.classList.toggle('is-expanded', isExpanded);
    this.classList.toggle('is-idle', state === 'idle');

    if (this.body) this.body.hidden = !isExpanded;
    if (this.configuredBar) this.configuredBar.hidden = !isConfigured;

    if (this.headerAction) this.headerAction.hidden = isConfigured || isExpanded;
    if (this.headerChevron) this.headerChevron.hidden = !isExpanded;
    if (this.headerEdit) this.headerEdit.hidden = !isConfigured;
    if (this.resetWrap) this.resetWrap.hidden = !isConfigured;

    if (this.headerToggle) {
      this.headerToggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }

    if (isConfigured && this.pendingResult && this.configuredPriceEl) {
      const pricing = this.pendingResult.pricing;
      const adjustmentNote = pricing.adjustmentCents > 0 ? ` (+${pricing.adjustmentFormatted})` : '';
      this.configuredPriceEl.textContent = `${pricing.grandTotalFormatted}${adjustmentNote}`;
    }

    this.updateHeaderCopy();
    this.updateHeaderIcons();

    this.dispatchEvent(
      new CustomEvent('fit-finder:state', {
        bubbles: true,
        detail: {
          state,
          configured: isConfigured,
          result: this.pendingResult,
          pricing: this.pendingResult?.pricing || null,
        },
      })
    );
  }

  saveMeasurementsPersistent(belly, thigh) {
    try {
      localStorage.setItem('ve_fit_finder_saved_measurements', JSON.stringify({ belly, thigh }));
    } catch {
      /* ignore */
    }
  }

  saveSession(result) {
    this.saveMeasurementsPersistent(result.belly, result.thigh);
    const mainVariantId = this.resolveMainVariantId();
    if (mainVariantId) {
      this.dataset.mainVariantId = String(mainVariantId);
      if (this.extensionPrices?.main) this.extensionPrices.main.variantId = mainVariantId;
    }
    try {
      if (mainVariantId) {
        sessionStorage.setItem('ve_fit_finder_main_variant_id', String(mainVariantId));
      }
      sessionStorage.setItem('ve_fit_finder_cart_items', JSON.stringify(result.cartItems));
      sessionStorage.setItem('ve_fit_finder_extension_summary', JSON.stringify(result.extensionSummary));
      sessionStorage.setItem('ve_fit_finder_measurements', JSON.stringify({ belly: result.belly, thigh: result.thigh }));
      sessionStorage.setItem('ve_fit_finder_pricing', JSON.stringify(result.pricing));
      sessionStorage.setItem('ve_fit_finder_needs_support', result.needsSupport ? '1' : '0');
      sessionStorage.setItem('ve_fit_finder_calculated', '1');
      sessionStorage.setItem('ve_fit_finder_result_type', result.resultType || '');
    } catch {
      /* ignore */
    }
  }

  clearSession() {
    try {
      sessionStorage.removeItem('ve_fit_finder_cart_items');
      sessionStorage.removeItem('ve_fit_finder_extension_summary');
      sessionStorage.removeItem('ve_fit_finder_measurements');
      sessionStorage.removeItem('ve_fit_finder_pricing');
      sessionStorage.removeItem('ve_fit_finder_needs_support');
      sessionStorage.removeItem('ve_fit_finder_calculated');
      sessionStorage.removeItem('ve_fit_finder_result_type');
      sessionStorage.removeItem('ve_fit_finder_main_variant_id');
    } catch {
      /* ignore */
    }
  }

  emitCalculated(result) {
    this.saveSession(result);

    this.dispatchEvent(
      new CustomEvent('fit-finder:calculated', {
        bubbles: true,
        detail: {
          cartItems: result.cartItems,
          extensionSummary: result.extensionSummary,
          pricing: result.pricing,
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('fit-finder:configured', {
        bubbles: true,
        detail: {
          belly: result.belly,
          thigh: result.thigh,
          summary: this.formatMeasuresSummary(result.belly, result.thigh),
          pricing: result.pricing,
          needsSupport: result.needsSupport,
          calculated: true,
        },
      })
    );
  }

  restoreFromSession() {
    let belly;
    let thigh;

    try {
      const saved = JSON.parse(localStorage.getItem('ve_fit_finder_saved_measurements') || 'null');
      if (saved?.belly != null && saved?.thigh != null) {
        belly = saved.belly;
        thigh = saved.thigh;
      }
    } catch {
      /* ignore */
    }

    if (belly == null || thigh == null) {
      try {
        const measurements = JSON.parse(sessionStorage.getItem('ve_fit_finder_measurements') || 'null');
        if (measurements) {
          belly = measurements.belly;
          thigh = measurements.thigh;
        }
      } catch {
        /* ignore */
      }
    }

    if (belly != null && thigh != null) {
      this.setMeasureValues(belly, thigh);
    } else {
      this.setMeasureValues(null, null);
    }

    if (!this.isCatalogReady()) {
      this.setUiState('idle');
      this.showStep('measure');
      return;
    }

    if (sessionStorage.getItem('ve_fit_finder_calculated') === '1') {
      const result = this.evaluate();
      try {
        const pricing = JSON.parse(sessionStorage.getItem('ve_fit_finder_pricing') || 'null');
        if (pricing) result.pricing = pricing;
      } catch {
        /* ignore */
      }
      this.pendingResult = result;
      this.setUiState('configured');
      this.dispatchEvent(
        new CustomEvent('fit-finder:configured', {
          bubbles: true,
          detail: {
            belly: result.belly,
            thigh: result.thigh,
            summary: this.formatMeasuresSummary(result.belly, result.thigh),
            pricing: result.pricing,
            needsSupport: result.needsSupport,
            calculated: true,
            restored: true,
          },
        })
      );
      return;
    }

    this.setUiState('idle');
    this.showStep('measure');
  }

  expand() {
    this.setUiState('expanded');
    this.showStep(this.currentStep || 'measure');
  }

  collapse() {
    if (this.dataset.state === 'configured') return;
    this.setUiState('idle');
  }

  onToggle() {
    if (!this.isCatalogReady()) return;
    if (this.dataset.state === 'configured') return;
    if (this.dataset.state === 'expanded') {
      this.collapse();
    } else {
      this.expand();
    }
  }

  onCalculate() {
    if (!this.isCatalogReady()) return;
    if (!this.hasValidMeasurements()) {
      this.showMeasureValidation();
      return;
    }
    this.clearMeasureValidation();
    const result = this.evaluate();
    this.pendingResult = result;
    this.saveMeasurementsPersistent(result.belly, result.thigh);

    this.renderResultStep(result);
    this.showStep('result');
    this.setUiState('expanded');
  }

  onSaveConfiguration() {
    const result = this.pendingResult || this.evaluate();
    if (result.needsSupport) return;
    this.pendingResult = result;
    this.emitCalculated(result);
    this.setUiState('configured');
  }

  onAddToCart() {
    const result = this.pendingResult || this.evaluate();
    if (result.needsSupport) return;
    this.pendingResult = result;
    this.emitCalculated(result);
    const mainVariantId = this.resolveMainVariantId();
    this.dispatchEvent(
      new CustomEvent('fit-finder:add-to-cart', {
        bubbles: true,
        detail: {
          cartItems: result.cartItems,
          mainVariantId,
          measurements: { belly: result.belly, thigh: result.thigh },
          pricing: result.pricing,
        },
      })
    );
  }

  onEditMeasures(evt) {
    if (!evt.target.closest('[data-action="edit-measures"]')) return;
    evt.preventDefault();
    if (this.currentStep === 'result') {
      this.showStep('measure');
    }
  }

  onEdit(evt) {
    evt.stopPropagation();
    if (!this.isCatalogReady()) return;
    this.expand();
    this.showStep('measure');
  }

  onReset(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    this.reset();
  }

  reset() {
    this.pendingResult = null;
    this.clearMeasureValidation();
    this.clearSession();
    this.setMeasureValues(null, null);
    this.showStep('measure');
    this.setUiState('idle');
    this.dispatchEvent(
      new CustomEvent('fit-finder:cleared', {
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('rb-fit-finder')) {
  customElements.define('rb-fit-finder', RbFitFinder);
}

document.addEventListener('fit-finder:reset', () => {
  document.querySelector('rb-fit-finder')?.reset?.();
});
